import { formatAddress, PortalModalNew } from '@servicetitan/cp-ui';
import { Button, Input, InputOnChangeData } from '@servicetitan/design-system';
import classNames from 'classnames';
import { FC, Fragment, useMemo, useState } from 'react';
import { BookingContextPair } from './booking-context';
import * as Styles from './booking-context-picker.module.less';

export interface BookingContextPickerProps {
    pairs: BookingContextPair[];
    onConfirm: (pair: BookingContextPair) => void;
    onDismiss: () => void;
}

/** A pair with its formatted address and lowercased search text computed once, not per keystroke. */
interface SearchablePair {
    pair: BookingContextPair;
    formattedAddress: string;
    searchText: string;
}

interface BookingContextGroup {
    customerId: number;
    customerName: string;
    items: SearchablePair[];
}

const toSearchablePair = (pair: BookingContextPair): SearchablePair => {
    const formattedAddress = pair.address ? formatAddress(pair.address) : '';
    return {
        pair,
        formattedAddress,
        searchText: `${pair.customerName} ${pair.locationName} ${formattedAddress}`.toLowerCase(),
    };
};

const groupByCustomer = (items: SearchablePair[]): BookingContextGroup[] => {
    const groupsByCustomerId = new Map<number, BookingContextGroup>();

    for (const item of items) {
        const { customerId, customerName } = item.pair;
        const existingGroup = groupsByCustomerId.get(customerId);
        if (existingGroup) {
            existingGroup.items.push(item);
        } else {
            groupsByCustomerId.set(customerId, { customerId, customerName, items: [item] });
        }
    }

    return [...groupsByCustomerId.values()];
};

/**
 * Shown only for 2+ bookable pairs. Selecting a row only marks it (enables
 * "Book Appointment"); confirming is what returns the selected pair to the
 * caller. Dismissing via the close control, "Cancel," or the backdrop calls
 * `onDismiss` only -- never `onConfirm`.
 */
export const BookingContextPicker: FC<BookingContextPickerProps> = ({
    onConfirm,
    onDismiss,
    pairs,
}) => {
    const [search, setSearch] = useState('');
    const [selectedPair, setSelectedPair] = useState<BookingContextPair | undefined>(undefined);

    const searchablePairs = useMemo(() => pairs.map(toSearchablePair), [pairs]);
    const visibleItems = useMemo(() => {
        const query = search.toLowerCase();
        return query
            ? searchablePairs.filter(item => item.searchText.includes(query))
            : searchablePairs;
    }, [searchablePairs, search]);
    const groups = useMemo(() => groupByCustomer(visibleItems), [visibleItems]);

    const handleSearchChange = (_event: unknown, data: InputOnChangeData) => {
        setSearch(data.value);
    };

    const handleConfirm = () => {
        if (selectedPair) {
            onConfirm(selectedPair);
        }
    };

    return (
        <PortalModalNew
            open
            title="Select a customer and location"
            onClose={onDismiss}
            footer={
                <Fragment>
                    <Button className="m-r-1" fill="outline" onClick={onDismiss}>
                        Cancel
                    </Button>
                    <Button primary disabled={!selectedPair} onClick={handleConfirm}>
                        Book Appointment
                    </Button>
                </Fragment>
            }
        >
            <Input
                icon="search"
                placeholder="Search by name or address"
                value={search}
                onChange={handleSearchChange}
                fluid
                className={Styles.search}
            />
            <div className={Styles.list}>
                {groups.map(group => (
                    <div key={group.customerId} className={Styles.group}>
                        <div className={Styles.groupHeader}>{group.customerName}</div>
                        {group.items.map(({ formattedAddress, pair }) => (
                            <button
                                key={`${pair.customerId}-${pair.locationId}`}
                                type="button"
                                className={classNames(Styles.row, {
                                    [Styles.rowSelected]:
                                        selectedPair?.locationId === pair.locationId &&
                                        selectedPair?.customerId === pair.customerId,
                                })}
                                onClick={() => setSelectedPair(pair)}
                            >
                                <span className={Styles.rowName}>{pair.locationName}</span>
                                <span className={Styles.rowAddress}>{formattedAddress}</span>
                            </button>
                        ))}
                    </div>
                ))}
            </div>
        </PortalModalNew>
    );
};
